import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

// E-posta adresini normalleştirme işlevi
const normalizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

const Login: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({});
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    let isValid = true;
    const newErrors: { email?: string; password?: string; general?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'E-posta adresi gereklidir';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Şifre gereklidir';
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = normalizeEmail(email);
      const { error } = await signIn(normalizedEmail, password);
      
      if (error) {
        setErrors({
          general: 'E-posta adresi veya şifre hatalı.'
        });
        return;
      }
    } catch (error: any) {
      setErrors({
        general: 'Giriş başarısız. Lütfen daha sonra tekrar deneyin.'
      });
    } finally {
      setLoading(false);
    }
  };

  const testSupabaseConnection = async () => {
    setTestResult('Test ediliyor...');
    try {
      // 1. Supabase bağlantısını test et
      const { data, error } = await supabase.from('users').select('count').limit(1);
      
      if (error) {
        setTestResult(`Supabase bağlantı hatası: ${error.message}`);
        return;
      }
      
      // 2. Mevcut oturumu kontrol et
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      
      // 3. Sonuçları göster
      let result = 'Test Sonuçları:\n';
      result += `- Supabase Bağlantısı: Başarılı\n`;
      result += `- Oturum Durumu: ${session ? 'Aktif' : 'Oturum açılmamış'}\n`;
      
      if (session) {
        result += `- Kullanıcı ID: ${session.user.id}\n`;
        result += `- Kullanıcı Email: ${session.user.email}\n`;
        
        // Rolü kontrol et
        const { data: userData, error: roleError } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();
          
        if (roleError) {
          result += `- Rol Kontrolü: Hata (${roleError.message})\n`;
        } else {
          result += `- Kullanıcı Rolü: ${userData.role}\n`;
        }
      }
      
      setTestResult(result);
    } catch (error: any) {
      setTestResult(`Test hatası: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Modern Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-blue-50 to-teal-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgZmlsbD0iI2YwZjlmZiIgZmlsbC1vcGFjaXR5PSIwLjQiPgo8cGF0aCBkPSJtMCA0MGw0MC00MGgtNDB2NDB6Ii8+CjwvZz4KPC9zdmc+')]"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
              <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 1.5c5.52 0 10 4.48 10 10 0 5.52-4.48 10-10 10s-10-4.48-10-10c0-5.52 4.48-10 10-10zm0 12.5c.83 0 1.5-.67 1.5-1.5S12.83 11 12 11s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm0-8c-2.76 0-5 2.24-5 5h2c0-1.66 1.34-3 3-3s3 1.34 3 3h2c0-2.76-2.24-5-5-5z" />
              </svg>
            </div>
            <h2 className="mt-8 text-4xl font-bold bg-gradient-to-r from-gray-900 via-emerald-800 to-blue-900 bg-clip-text text-transparent">
              Tekrar Hoş Geldiniz
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Halısaha rezervasyonlarınız için giriş yapın
            </p>
          </div>

          {/* Form */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
            {errors.general && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-xl">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl bg-gray-50/50 backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-0 ${
                    errors.email 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-200 focus:border-emerald-500 focus:bg-white'
                  }`}
                  placeholder="E-posta adresiniz"
                />
                <label 
                  htmlFor="email" 
                  className={`absolute left-12 transition-all duration-200 pointer-events-none ${
                    email 
                      ? '-top-2 text-xs bg-white px-2 text-emerald-600 font-medium' 
                      : 'top-4 text-gray-500'
                  }`}
                >
                  {email ? 'E-posta Adresi' : ''}
                </label>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl bg-gray-50/50 backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-0 ${
                    errors.password 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-200 focus:border-emerald-500 focus:bg-white'
                  }`}
                  placeholder="Şifreniz"
                />
                <label 
                  htmlFor="password" 
                  className={`absolute left-12 transition-all duration-200 pointer-events-none ${
                    password 
                      ? '-top-2 text-xs bg-white px-2 text-emerald-600 font-medium' 
                      : 'top-4 text-gray-500'
                  }`}
                >
                  {password ? 'Şifre' : ''}
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2 transition-all duration-200"
                  />
                  <span className="ml-2 text-sm text-gray-600">Beni hatırla</span>
                </label>
                
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-emerald-600 hover:text-emerald-500 font-medium transition-colors duration-200"
                >
                  Şifremi unuttum
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 text-white py-4 px-6 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span>Giriş Yap</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-8 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Hesabınız yok mu?</span>
              </div>
            </div>

            {/* Sign Up Link */}
            <div className="mt-6">
              <Link
                to="/signup"
                className="w-full bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-2xl font-semibold text-lg hover:border-emerald-300 hover:text-emerald-600 transition-all duration-200 flex items-center justify-center space-x-2 group"
              >
                <span>Hesap Oluştur</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </div>

          {/* Features */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span>Güvenli Ödeme</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span>Anında Rezervasyon</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span>7/24 Destek</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;